import React, { useState, useEffect } from 'react';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { profileDefinitions } from './profileDefinitions';

function ProfileTooltip({ profileKey }) {
    return (
        <Tooltip title={<span style={{ fontSize: '1rem' }}>{profileDefinitions[profileKey]}</span>}arrow>
            <HelpOutlineIcon style={{ fontSize: '16px', marginLeft: '5px', cursor: 'pointer', color: '#007BFF' }} />
        </Tooltip>
    );
  }

const sliderOptions = {
    ew: {
        1: { label: 'High Engage (Fight)', description: 'Highly engaged in interactions, proactively addressing challenges and conflicts.' },
        2: { label: 'Engage (Fight)', description: 'Prefers engaging with challenges directly, focusing on active participation.' },
        3: { label: 'Balanced', description: 'Balances engaging and withdrawing behaviors, depending on the situation.' },
        4: { label: 'Withdraw (Flight)', description: 'Prefers stepping back from direct confrontation, focusing on avoiding conflict.' },
        5: { label: 'High Withdraw (Flight)', description: 'Highly withdrawn from interactions, avoiding conflict or confrontation.' },
    },
    ac: {
        1: { label: 'Highly Accepting', description: 'Strongly adapts to the environment, prioritizing harmony and creative use of resources.' },
        2: { label: 'Accepting', description: 'Prefers to work within established conditions and adapt to existing structures.' },
        3: { label: 'Balanced', description: 'Balances acceptance and control, adapting as needed or asserting when required.' },
        4: { label: 'Controlling', description: 'Prefers to shape their environment to achieve their desired outcomes.' },
        5: { label: 'Highly Controlling', description: 'Strongly prefers to act on (or change) the environment to meet their desired outcomes.' },
    },
    ei: {
        1: { label: 'Highly Extroverted', description: 'Thrives on social interaction, expressing thoughts and emotions openly.' },
        2: { label: 'Extroverted', description: 'Leans toward extroversion, engaging easily in social settings and sharing ideas.' },
        3: { label: 'Balanced', description: 'Balances extroverted and introverted tendencies, adapting to the context.' },
        4: { label: 'Introverted', description: 'Prefers introspection, expressing thoughts selectively in quieter environments.' },
        5: { label: 'Highly Introverted', description: 'Deeply values introspection and privacy, often avoiding social interactions.' },
    },
    di: {
        1: { label: 'Highly Direct', description: 'Highly values clarity and brevity, prioritizing straightforward communication.' },
        2: { label: 'Direct', description: 'Favors direct communication, expressing thoughts clearly and concisely.' },
        3: { label: 'Balanced', description: 'Balances direct and indirect communication, adapting to the audience.' },
        4: { label: 'Indirect', description: 'Takes a roundabout approach, valuing context and detail in communication.' },
        5: { label: 'Highly Indirect', description: 'Prefers detailed and subtle communication, relying on context and nuance.' },
    },
    pj: {
        1: { label: 'Highly Perceiving', description: 'Highly open to new ideas, embracing adaptability and multiple perspectives.' },
        2: { label: 'Perceiving', description: 'Values flexibility and gathering input from various sources before deciding.' },
        3: { label: 'Balanced', description: 'Balances perceiving and judging tendencies, adapting to situational demands.' },
        4: { label: 'Judging', description: 'Emphasizes structure and decisiveness in evaluating and acting on situations.' },
        5: { label: 'Highly Judging', description: 'Strongly favors structure and decisive approaches, acting on well-defined expectations.' },
    },
    rt: {
        1: { label: 'Highly Risk-Taking', description: 'Highly adventurous and eager to explore new opportunities, embracing uncertainty.' },
        2: { label: 'Risk-Taking', description: 'Prefers action and discovery, focusing on learning through experience.' },
        3: { label: 'Balanced', description: 'Balances risk-taking and risk-assessing approaches based on situational needs.' },
        4: { label: 'Risk-Assessing', description: 'Carefully considers actions, ensuring understanding before proceeding.' },
        5: { label: 'Highly Risk-Assessing', description: 'Strongly cautious and meticulous, avoiding unnecessary risks and prioritizing planning.' },
    },
    op: {
        1: { label: 'Highly Optimistic', description: 'Consistently positive, viewing challenges as opportunities and expecting favorable outcomes.' },
        2: { label: 'Optimistic', description: 'Leans toward optimism, showing hopefulness and confidence despite challenges.' },
        3: { label: 'Balanced', description: 'Balances optimism and pessimism, adapting perspective based on the situation.' },
        4: { label: 'Pessimistic', description: 'Focuses on potential downsides, approaching situations with caution and skepticism.' },
        5: { label: 'Highly Pessimistic', description: 'Deeply cautious and skeptical, often expecting unfavorable outcomes.' },
    },
    co: {
        1: { label: 'Highly Change-Oriented', description: 'Thrives on novelty and actively seeks innovative solutions and opportunities for change.' },
        2: { label: 'Change-Oriented', description: 'Shows a preference for adapting and exploring new ideas.' },
        3: { label: 'Balanced', description: 'Balances change and continuity, adapting as necessary while appreciating stability.' },
        4: { label: 'Continuity-Oriented', description: 'Prefers consistent and predictable environments, maintaining routines.' },
        5: { label: 'Highly Continuity-Oriented', description: 'Strongly values tradition and stability, avoiding unnecessary disruptions.' },
    },
    tp: {
        1: { label: 'Highly Task-Focused', description: 'Prioritizes efficiency, productivity, and achieving goals above personal interactions or relationships.' },
        2: { label: 'Task-Focused', description: 'Shows a preference for task-oriented activities, focusing on objectives and results while considering interpersonal dynamics.' },
        3: { label: 'Balanced', description: 'Balances task and people focus, valuing both achieving goals and fostering positive relationships.' },
        4: { label: 'People-Focused', description: 'Shows a preference for people-oriented activities, emphasizing relationships and harmony while working toward objectives.' },
        5: { label: 'Highly People-Focused', description: 'Places strong importance on relationships, collaboration, and fostering a sense of community over achieving specific task-related goals.' },
    },
};

const UserProfile = () => {
    const [profileData, setProfileData] = useState({
      firstName: '',
      lastName: '',
      jobTitle: '',
      organization: '',
      isDispositionEnabled: false,
      dispositionalTraits: {
        ew: 3,
        ac: 3,
        ei: 3,
        di: 3,
        pj: 3,
        rt: 3,
        op: 3,
        co: 3,
        tp: 3,
      },
    });

    const fetchProfile = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/profile-pull`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            });

            if (!response.ok) {
            throw new Error('Failed to fetch profile data');
            }

            const data = await response.json();

            // Ensure `dispositionalTraits` contains all keys
            const allTraits = {
                ew: 3,
                ac: 3,
                ei: 3,
                di: 3,
                pj: 3,
                rt: 3,
                op: 3,
                co: 3,
                tp: 3,
            };
            const mergedTraits = { ...allTraits, ...(data.dispositionalTraits || {}) };

            // Ensure `dispositionalTraits` exists
            setProfileData({
                ...data,
                isDispositionEnabled: !!data.isDispositionEnabled,
                dispositionalTraits: mergedTraits,
            });

        } catch (error) {
            console.error('Error fetching profile data:', error);
        }
    };

    const updateProfile = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/profile-push`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(profileData),
            });

            if (!response.ok) {
                throw new Error('Failed to update profile');
            }

            alert('Profile updated successfully!');
        } catch (error) {
            console.error('Error updating profile:', error);
        }
    };

    useEffect(() => {
    fetchProfile();
    }, []);

    const handleInputChange = (field, value) => {
    setProfileData({ ...profileData, [field]: value });
    };

    const handleTraitChange = (trait, value) => {
        setProfileData((prevState) => ({
            ...prevState,
            dispositionalTraits: {
                ...prevState.dispositionalTraits,
                [trait]: value, // Ensure the updated trait is merged with the existing object
            },
        }));
    };

    const styles = {
        formContainer: {
            maxWidth: '800px',
            margin: '20px auto',
            padding: '20px',
            background: '#f9f9f9',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        },
        header: {
            textAlign: 'center',
            color: '#333',
            marginBottom: '20px',
        },
        section: {
            border: '1px solid #ccc',
            padding: '20px', // Adds padding between content and border
            marginBottom: '20px',
            borderRadius: '5px',
        },
        inputGroup: {
            marginBottom: '15px',
            marginTop: '10px',
        },
        label: {
            display: 'block',
            marginBottom: '5px',
            fontWeight: 'bold',
            color: '#555',
        },
        input: {
            width: 'calc(100% - 20px)',
            padding: '10px',
            borderRadius: '5px',
            border: '1px solid #ccc',
            fontSize: '16px',
        },
        checkboxContainer: {
            marginBottom: '15px',
        },
        checkboxLabel: {
            display: 'flex',
            alignItems: 'center',
            fontSize: '16px',
            color: '#555',
        },
        checkbox: {
            marginRight: '10px',
        },
        button: {
            display: 'block',
            width: '100%',
            padding: '10px',
            backgroundColor: '#6c63ff',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '20px',
        },
        buttonHover: {
            backgroundColor: '#5a54e0',
        },
        sliderContainer: {
            margin: '20px',
        },
        sliderBackground: {
            background: '#f9f9f9', // Light grey background
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px', // Spacing between sliders
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)', // Optional for subtle shadow
        },
        slider: {
            width: '100%',
            margin: '10px 0',
            height: '6px',
            borderRadius: '10px',
            appearance: 'none', // Remove default browser styles
            outline: 'none',
            position: 'relative',
        },
        tickContainer: {
            position: 'absolute',
            width: '100%',
            height: '10px',
            top: '22px', // Position tick marks below the slider
            display: 'flex',
            justifyContent: 'space-between',
        },
        tick: {
            position: 'absolute',
            bottom: '-8px', // Adjust vertical position relative to the slider
            fontSize: '16px', // Tick mark size
            color: '#333', // Tick mark color
            transform: 'translateX(-50%)', // Center the tick mark at its position
        },
        description: {
            fontSize: '16px',
            color: '#666',
            marginBottom: '10px'
        },
    };

return (
    <div className="max-w-3xl mx-auto p-8 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Update Profile</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">First Name:</label>
            <input
                type="text"
                value={profileData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            </div>

            <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Last Name:</label>
            <input
                type="text"
                value={profileData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            </div>

            <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Job Title:</label>
            <input
                type="text"
                value={profileData.jobTitle}
                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            </div>

            <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Organization:</label>
            <input
                type="text"
                value={profileData.organization}
                onChange={(e) => handleInputChange('organization', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            </div>
        </div>

        <div className="mt-6">
            <label className="inline-flex items-center text-sm text-gray-700">
            <input
                type="checkbox"
                checked={profileData.isDispositionEnabled}
                onChange={(e) =>
                setProfileData((prev) => ({
                    ...prev,
                    isDispositionEnabled: e.target.checked,
                }))
                }
                className="form-checkbox h-4 w-4 text-purple-600"
            />
            <span className="ml-2">Enable Disposition Traits</span>
            </label>
        </div>

        {/* Dispositional Traits Section */}
        {profileData.isDispositionEnabled && (
            <div className="mt-10 bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Personality Profiles – Disposition</h2>
                <div className="space-y-8">
                    {Object.keys(sliderOptions).map((profile) => {
                        const selectedValue = profileData.dispositionalTraits?.[profile] || 3;
                        const profileOption = sliderOptions[profile][selectedValue];
                        const profileDetails = {
                            ew: { left: 'Engage', right: 'Withdraw' },
                            ac: { left: 'Accept', right: 'Control' },
                            ei: { left: 'Extrovert', right: 'Introvert' },
                            di: { left: 'Direct', right: 'Indirect' },
                            pj: { left: 'Perceive', right: 'Judge' },
                            rt: { left: 'Risk-Take', right: 'Risk-Assess' },
                            op: { left: 'Optimist', right: 'Pessimist' },
                            co: { left: 'Change-Oriented', right: 'Continuity-Oriented' },
                            tp: { left: 'Task-Focused', right: 'People-Focused' },
                        }[profile];

                        const labelOffset = (selectedValue - 1) * 25;
                        const labelStyle = {
                            position: 'relative',
                            left: selectedValue === 1 ? '0%' : selectedValue === 5 ? '100%' : `${labelOffset}%`,
                            transform: selectedValue === 1 ? 'none' : selectedValue === 5 ? 'translateX(-100%)' : 'translateX(-50%)',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#6c63ff',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            textAlign: 'center',
                            display: 'inline-block',
                            width: 'fit-content',
                        };

                        return (
                            <div key={profile} className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200">
                                {/* Spectrum Titles Above the Slider */}
                                <div className="flex justify-between items-center font-semibold mb-1">
                                    <div className="flex items-center gap-1">
                                    <span>{profileDetails.left}</span>
                                    <ProfileTooltip profileKey={profile} />
                                    </div>
                                    <span>{profileDetails.right}</span>
                                </div>

                                {/* Custom Slider Container */}
                                <div style={{ position: 'relative', height: '40px' }}>
                                    <input
                                    type="range"
                                    min="1"
                                    max="5"
                                    value={selectedValue}
                                    onChange={(e) => handleTraitChange(profile, parseInt(e.target.value, 10))}
                                    style={{
                                        ...styles.slider,
                                        background:
                                        selectedValue === 1
                                            ? `linear-gradient(to right, #81c345 25%, grey 50%)`
                                            : selectedValue === 2
                                            ? `linear-gradient(to right, grey 0%, grey 25%, #81c345 25%, grey 50%)`
                                            : selectedValue === 3
                                            ? `linear-gradient(to right, grey 0%, grey 100%)`
                                            : selectedValue === 4
                                            ? `linear-gradient(to left, grey 0%, grey 25%, #81c345 25%, grey 50%)`
                                            : `linear-gradient(to left, #81c345 25%, grey 50%)`,
                                    }}
                                    />
                                    {/* Custom Tick Marks */}
                                    <div style={styles.tickContainer}>
                                    {[1, 2, 3, 4, 5].map((tick, index) => (
                                        <div
                                        key={tick}
                                        style={{
                                            ...styles.tick,
                                            left: `${1.5 + index * 24.25}%`,
                                        }}
                                        >
                                        ^
                                        </div>
                                    ))}
                                    </div>
                                </div>

                                {/* Label Below the Slider with Hover Tooltip */}
                                <div style={labelStyle}>
                                    <span className="relative group cursor-pointer text-purple-700 font-semibold">
                                    {profileOption?.label}
                                    <span className="absolute bottom-full mb-2 hidden group-hover:block bg-white text-sm text-gray-800 rounded-lg border border-gray-300 shadow-md px-4 py-2 w-64 z-50">
                                        {profileOption?.description}
                                    </span>
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        )}
        <button style={styles.button} onClick={updateProfile}>Save Changes</button>
    </div>
  );
};

export default UserProfile;